import { useMemo, useState } from "react";
import {
  Plus,
  MoreHorizontal,
  Trash2,
  Activity,
  Wrench,
  MapPin,
  Loader2,
  Download,
} from "lucide-react";
import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DataTableRow,
  DataTableRowCheckbox,
  DataTableSelectAllCheckbox,
} from "@/components/shared/DataTableRow";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import {
  ListPageShell,
  useServerListFilter,
} from "@/components/shared/ListPageShell";
import { cn } from "@/lib/utils";
import { trpcClient } from "@/lib/trpc";
import { toast } from "sonner";
import type { Machine } from "@/shared/schemas";
import { StatusBadge } from "@/components/shared/StatusBadge";
import { ExportImportButtons } from "@/components/shared/ExportImportButtons";
import { ImportDialog } from "@/components/shared/ImportDialog";
import { useExcelExport } from "@/hooks/useExcelExport";
import { downloadExcelTemplate, type ExcelColumnDef } from "@/lib/excel.utils";
import type { FilterField } from "@/components/shared/FilterBar";
import { PageFilterTags } from "@/components/shared/PageFilterTags";
import {
  InlineFilterRow,
  CHECKBOX_COL_NULL,
} from "@/components/shared/InlineFilterRow";
import { SortableHeader } from "@/components/shared/SortableHeader";
import { MachineType } from "@/config/status-registry";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { useRowSelection } from "@/hooks/useRowSelection";
import { useRoleAccess } from "@/hooks/useRoleAccess";
import {
  BulkActionBar,
  type BulkAction,
} from "@/components/shared/BulkActionBar";
import { executeBulkAction } from "@/utils/bulkActionUtils";
import { useConfirm } from "@/providers/ConfirmProvider";
import { useImportRefs } from "@/hooks/useImportRefs";
import { findByCode, runUpsertImport } from "@/lib/import-kit";

/** Cột Excel thiết bị — dùng chung cho xuất, file mẫu và nhập (04/11/2026); Loại máy / Tổ SX khớp danh mục */
const EXPORT_COLUMNS: ExcelColumnDef[] = [
  { key: "machineId", label: "Mã máy", width: 16, required: true },
  { key: "name", label: "Tên máy", width: 25, required: true },
  { key: "type", label: "Loại máy", width: 18, required: true, ref: "machineType" },
  { key: "department.name", label: "Tổ SX", width: 16, ref: "department" },
  { key: "status", label: "Trạng thái", width: 16, type: "enum", enumValues: ["IDLE", "RUNNING", "MAINTENANCE", "DOWN"] },
];

const FILTER_FIELDS: FilterField[] = [
  { key: "machineId", label: "Mã máy", type: "text" },
  { key: "name", label: "Tên máy", type: "text" },
  {
    key: "type",
    label: "Loại",
    type: "combobox-multi",
    options: [
      { value: "OFFSET_PRINTER", label: "Máy in Offset" },
      { value: "FLEXO_PRINTER", label: "Máy in Flexo" },
      { value: "PAPER_CUTTER", label: "Máy xén giấy" },
      { value: "COATER", label: "Máy tráng phủ" },
      { value: "SILK_SCREEN", label: "Máy in lụa" },
      { value: "LAMINATOR", label: "Máy cán màng" },
      { value: "HOT_FOIL", label: "Máy ép kim" },
      { value: "DIE_CUTTER", label: "Máy bế" },
      { value: "WINDOW_PATCHER", label: "Máy dán cửa sổ" },
      { value: "FOLDER_GLUER", label: "Máy gấp dán" },
      { value: "PACKING", label: "Máy đóng gói" },
      { value: "OTHER", label: "Khác" },
    ],
  },
  { key: "department", label: "Tổ SX", type: "text" },
  {
    key: "status",
    label: "Trạng thái",
    type: "combobox-multi",
    options: [
      { value: "IDLE", label: "Chờ việc" },
      { value: "RUNNING", label: "Đang chạy" },
      { value: "MAINTENANCE", label: "Bảo trì" },
      { value: "DOWN", label: "Hỏng máy" },
    ],
  },
];

export default function MachinesPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { hasPermission } = useRoleAccess();

  const [showImport, setShowImport] = useState(false);

  // Server-side Data + Pagination
  const {
    items: machines,
    paginatedItems: rawPaginated,
    isLoading,
    error,
    filterValues,
    serverParams,
    showFilter,
    onFilterChange,
    onToggleFilter,
    onClearAll,
    currentPage: listPage,
    pageSize: listPageSize,
    totalItems,
    onPageChange,
    onPageSizeChange,
    sortField,
    sortOrder,
    handleSort,
  } = useServerListFilter<any>({
    queryKey: "machines",
    queryFn: (params) => trpcClient.machines.list.query(params) as any,
    defaultLimit: 500,
  });

  const paginatedMachines = rawPaginated as Machine[];

  const rowIds = paginatedMachines.map((m) => m.machineId);
  const {
    selectedIds,
    selectedCount,
    clearSelection,
    selectionMode,
    toggleSelect,
    handleRowSelect,
    toggleSelectAll,
    handleRowMouseDown,
    handleRowMouseUp,
    handleRowMouseLeave,
    wasLongPress,
    allSelected,
  } = useRowSelection(rowIds);

  const { handleExport, isExporting } = useExcelExport({
    filename: "danh_sach_thiet_bi",
    sheetName: "Thiết Bị",
    columns: EXPORT_COLUMNS,
    queryFn: (params) => trpcClient.machines.list.query(params),
    currentFilters: serverParams,
    selectedIds: Array.from(selectedIds),
    idKey: "machineId",
  });

  // Nhập Excel thiết bị (04/11/2026): mã máy đã có → cập nhật, mới → tạo; báo cáo từng dòng (trước đây lỗi bị nuốt)
  const importRefs = useImportRefs();
  const machineImportRefs = useMemo(
    () => ({ machineType: importRefs.machineType, department: importRefs.department("id") }),
    [importRefs],
  );
  const handleImport = async (rows: Record<string, unknown>[]) => {
    const result = await runUpsertImport(rows, {
      codeOf: (row) => String(row.machineId ?? "").trim(),
      findExisting: findByCode(() => trpcClient.machines.list.query({ limit: 10000 } as any), (m) => m.machineId),
      prepare: (row, mode) => {
        const { "department.name": departmentId, machineId, ...rest } = row as any;
        const data: Record<string, unknown> = { ...rest };
        if (departmentId !== undefined) data.departmentId = departmentId || null;
        if (!data.status) delete data.status;
        if (mode === "create") data.machineId = machineId;
        return data;
      },
      create: (data) => trpcClient.machines.create.mutate(data as any),
      update: (id, data) => trpcClient.machines.update.mutate({ id, data } as any),
    });
    await queryClient.invalidateQueries({ queryKey: ["machines"] });
    return result;
  };

  // Mutation cho create/update


  // Mutation cho delete
  const deleteMutation = useMutation({
    mutationFn: (id: string) => trpcClient.machines.delete.mutate({ id }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["machines"] });
    },
    onError: async () => {
      toast.error("Không thể xóa thiết bị");
    },
  });

  const getBulkActions = (): BulkAction[] => {
    if (!selectedCount) return [];
    const actions: BulkAction[] = [];

    if (hasPermission("machines", "delete")) {
      actions.push({
        icon: <Trash2 className="h-4 w-4" />,
        label: `Xoá (${selectedCount})`,
        onClick: () => {
          executeBulkAction({
            confirmFn: confirm,
            selectedIds: Array.from(selectedIds),
            actionFn: async (id) => trpcClient.machines.delete.mutate({ id }),
            actionName: "xóa",
            itemType: "máy móc",
            onSuccess: () => {
              clearSelection();
              queryClient.invalidateQueries({ queryKey: ["machines"] });
            },
            requireConfirm: true,
            confirmMessage: `Bạn có chắc muốn xóa ${selectedCount} máy móc đã chọn?`,
          });
        },
        variant: "destructive",
      });
    }
    actions.push({
      label: "Xuất Excel",
      icon: <Download className="w-3.5 h-3.5" />,
      onClick: () => {
        handleExport();
        clearSelection();
      },
    });
    return actions;
  };

  // Status mutation available for future use
  const _statusMutation = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      trpcClient.machines.updateStatus.mutate({ id, status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["machines"] });
    },
    onError: async () => {
      toast.error("Không thể cập nhật trạng thái");
    },
  });



  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-500">Đang tải dữ liệu...</span>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="p-8 text-center text-red-500">
        Lỗi khi tải dữ liệu thiết bị. Vui lòng thử lại.
        <Button className="mt-4" onClick={() => window.location.reload()}>
          Thử lại
        </Button>
      </div>
    );
  }

  return (
    <>
      <ListPageShell
        trashModule="machines"
        unitLabel="TB"
        headerFilters={
          <PageFilterTags
            items={[
              { key: "ALL", label: "Tất cả" },
              { key: "IDLE", label: "Chờ việc" },
              { key: "RUNNING", label: "Đang chạy" },
              { key: "MAINTENANCE", label: "Bảo trì" },
              { key: "DOWN", label: "Hỏng máy" },
            ]}
            selectedKeys={
              typeof filterValues.status === "string"
                ? [filterValues.status || "ALL"]
                : Array.isArray(filterValues.status) && filterValues.status.length > 0
                  ? (filterValues.status as string[])
                  : ["ALL"]
            }
            onSelect={(key) =>
              onFilterChange({
                ...filterValues,
                status: !key || key === "ALL" ? "" : key,
              })
            }
            dropdownLabel="Trạng thái"
          />
        }
        actions={
          <ExportImportButtons
            onExport={() => handleExport()}
            exportLoading={isExporting}
            onImportClick={() => setShowImport(true)}
            onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "thiet_bi")}
          >
            {hasPermission("machines", "create") && (
              <Button
                size="sm"
                className="h-8 text-xs"
                onClick={() => navigate({ to: "/machines/new" })}
              >
                <Plus size={14} className="mr-1" /> Thêm TB
              </Button>
            )}
          </ExportImportButtons>
        }
        filterFields={FILTER_FIELDS}
        filterValues={filterValues}
        showFilter={showFilter}
        onToggleFilter={onToggleFilter}
        onClearAll={onClearAll}
        currentPage={listPage}
        pageSize={listPageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      >
        <div className="overflow-x-auto apv-compact-table">
          <Table className={cn(selectionMode && "apv-select-mode")}>
            <TableHeader className="apv-table-header">
              <TableRow className="border-b-0">
                <DataTableSelectAllCheckbox
                  rowSelection={
                    { allSelected, toggleSelectAll, selectionMode } as any
                  }
                />
                <SortableHeader
                  label="Mã máy"
                  field="machineId"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="apv-col-code w-[120px]"
                />
                <SortableHeader
                  label="Tên máy & Loại"
                  field="name"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Tổ sản xuất"
                  field="department"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[120px]"
                />
                <TableHead className="w-[120px] font-semibold  text-[11px] uppercase py-3">
                  Thông số chính
                </TableHead>
                <TableHead className="w-[180px] font-semibold  text-[11px] uppercase py-3">
                  QT liên quan
                </TableHead>
                <SortableHeader
                  label="Trạng thái"
                  field="status"
                  currentField={sortField}
                  currentOrder={sortOrder}
                  onSort={handleSort}
                  className="w-[100px] text-center"
                />
                <TableHead className="w-[50px] px-4"></TableHead>
              </TableRow>
              {showFilter && (
                <InlineFilterRow
                  columns={[
                    CHECKBOX_COL_NULL,
                    { key: "machineId" },
                    { key: "name" },
                    { key: "department" },
                    null,
                    null,
                    { key: "status" },
                    null,
                  ]}
                  filterFields={FILTER_FIELDS}
                  filterValues={filterValues}
                  onFilterChange={onFilterChange}
                />
              )}
            </TableHeader>
            <TableBody>
              {paginatedMachines.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={8}
                    className="h-24 text-center text-slate-500"
                  >
                    Không tìm thấy thiết bị nào.
                  </TableCell>
                </TableRow>
              ) : (
                paginatedMachines.map((m) => (
                  <DataTableRow
                    key={m.machineId}
                    id={m.machineId}
                    rowSelection={
                      {
                        selectedIds,
                        handleRowMouseDown,
                        handleRowMouseUp,
                        handleRowMouseLeave,
                        wasLongPress,
                        selectionMode,
                        handleRowSelect,
                      } as any
                    }
                    onClick={async () => {
                      if (selectionMode) return;
                      navigate({
                        to: "/machines/$machineId",
                        params: { machineId: m.machineId },
                      });
                    }}
                  >
                    <DataTableRowCheckbox
                      id={m.machineId}
                      rowSelection={{ selectedIds, toggleSelect } as any}
                    />
                    <TableCell className="apv-col-code font-mono text-[13px] font-bold text-slate-400 py-1">
                      {m.machineId}
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex flex-col">
                        <span className="font-semibold text-slate-900 leading-tight">
                          {m.name}
                        </span>
                        <span
                          className={cn(
                            "text-[10px] font-bold uppercase tracking-wider",
                            MachineType[
                              m.type as keyof typeof MachineType
                            ]?.color
                              ?.replace("bg-", "text-")
                              .replace("-100", "-600") || "text-slate-400",
                          )}
                        >
                          {MachineType[m.type as keyof typeof MachineType]
                            ?.label || m.type.replace("_", " ")}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex items-center gap-1.5 text-slate-600 font-medium text-[13px]">
                        <MapPin className="w-3.5 h-3.5 text-indigo-400" />
                        {(m.department as any)?.name ||
                          (m as any).departmentId ||
                          m.department ||
                          "—"}
                      </div>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="text-[11px] text-slate-500 font-mono">
                        {m.specs ? (
                          <div className="flex flex-wrap gap-x-2">
                            {Object.entries(m.specs)
                              .slice(0, 2)
                              .map(([key, val]) => (
                                <span key={key} title={key}>
                                  {val}
                                </span>
                              ))}
                          </div>
                        ) : (
                          <span className="text-slate-300 italic">
                            No specs
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="py-1">
                      <div className="flex flex-wrap gap-1">
                        {((m as any).machineProcesses || [])
                          .slice(0, 3)
                          .map((mp: any) => (
                            <Badge
                              key={mp.process?.id || mp.id}
                              variant="outline"
                              className="text-[9px] px-1 py-0 bg-emerald-50 text-emerald-700 border-emerald-200"
                            >
                              {mp.process?.name || "—"}
                            </Badge>
                          ))}
                        {((m as any).machineProcesses || []).length > 3 && (
                          <Badge
                            variant="outline"
                            className="text-[9px] px-1 py-0"
                          >
                            +{(m as any).machineProcesses.length - 3}
                          </Badge>
                        )}
                        {((m as any).machineProcesses || []).length === 0 && (
                          <span className="text-xs text-slate-300">—</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-center py-1">
                      <div className="flex justify-center">
                        <StatusBadge type="MachineStatus" value={m.status} />
                      </div>
                    </TableCell>
                    <TableCell
                      className="px-4 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-1">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button
                              variant="ghost"
                              className="h-7 w-7 p-0 hover:bg-slate-200/50"
                            >
                              <MoreHorizontal className="h-4 w-4 text-slate-400" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent
                            align="end"
                            className="w-[180px] shadow-lg border-slate-200"
                          >
                            <DropdownMenuLabel className="text-xs font-semibold text-slate-500">
                              Thao tác
                            </DropdownMenuLabel>
                            {/* OEE và lịch bảo trì là các tab trên trang chi tiết thiết bị */}
                            <DropdownMenuItem
                              className="text-[13px] py-2 cursor-pointer"
                              onClick={() => navigate({ to: "/machines/$machineId", params: { machineId: m.machineId } })}
                            >
                              <Activity className="w-4 h-4 mr-2 text-indigo-400" />{" "}
                              Theo dõi OEE
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-[13px] py-2 cursor-pointer"
                              onClick={() => navigate({ to: "/machines/$machineId", params: { machineId: m.machineId } })}
                            >
                              <Wrench className="w-4 h-4 mr-2 text-slate-400" />{" "}
                              Lịch bảo trì
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-100" />
                            {hasPermission("machines", "delete") && (
                              <DropdownMenuItem
                                className="text-[13px] py-2 text-rose-600 focus:text-rose-600 focus:bg-rose-50 cursor-pointer"
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (
                                    await confirm(
                                      "Bạn có chắc chắn muốn thanh lý thiết bị này?",
                                    )
                                  ) {
                                    deleteMutation.mutate(m.machineId);
                                  }
                                }}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Thanh lý
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </TableCell>
                  </DataTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        <BulkActionBar
          selectedCount={selectedCount}
          onClearSelection={clearSelection}
          actions={getBulkActions()}
        />
      </ListPageShell>




      <ImportDialog
        title="Nhập Thiết bị"
        open={showImport}
        onOpenChange={setShowImport}
        columns={EXPORT_COLUMNS}
        onImport={handleImport}
        refs={machineImportRefs}
        onDownloadTemplate={() => downloadExcelTemplate(EXPORT_COLUMNS, "thiet_bi")}
      />
    </>
  );
}
